import { useMemo } from 'react';
import { PackagePlus } from 'lucide-react';
import type { ReceiptTypeReport, ReportRange } from '@eombor/contracts';
import type { UseQueryResult } from '@tanstack/react-query';
import type { ApiClientError } from '../../../shared/api/client.js';
import { t } from '../../../shared/i18n/index.js';
import type { StackedPoint, StackedSeries } from '../model/chart-data.js';
import { ReportCard } from './ReportCard.js';
import { StackedBars } from './StackedBars.js';

/** `StockFlowBlock` dagi bilan ayni qoida — yorliqlar bir-birini bosmasin. */
const MAX_LABELS = 12;

const shortDate = (iso: string): string => {
  const parts = iso.split('-');
  return `${parts[2] ?? ''}.${parts[1] ?? ''}`;
};

const format = (value: number): string => value.toLocaleString('ru-RU');

export interface ReceiptTypeBlockProps {
  query: UseQueryResult<ReceiptTypeReport, ApiClientError>;
  range: ReportRange;
}

/**
 * TUSHUM TURLARI.
 *
 * Savol «qancha keldi» emas (unga kirim/chiqim grafigi javob beradi),
 * balki «QAYERDAN keldi»: Excel bilan kirim qilingan hujjatmi, qaytib
 * kelgan talabnomami yoki inventarizatsiya tuzatishimi. Uchovi butunlay
 * boshqa hodisa va bittasining o'sishi butunlay boshqa ma'no beradi —
 * tuzatishlarning ko'payishi, masalan, hisobda muammo borligini aytadi.
 */
export const ReceiptTypeBlock = ({ query, range }: ReceiptTypeBlockProps) => {
  const series = useMemo<StackedSeries[]>(
    () => [
      { key: 'receipt', label: t('report.receiptDoc'), color: 'var(--color-accent)' },
      { key: 'requisition', label: t('report.receiptReturn'), color: 'var(--color-info)' },
      { key: 'adjustment', label: t('report.receiptAdjust'), color: 'var(--color-warn)' },
    ],
    [],
  );

  const points = useMemo<StackedPoint[]>(() => {
    const rows = query.data?.series ?? [];
    const step = Math.max(1, Math.ceil(rows.length / MAX_LABELS));
    return rows.map((point, index) => ({
      label: index % step === 0 ? shortDate(point.date) : '',
      values: {
        receipt: point.receipt,
        requisition: point.requisition,
        adjustment: point.adjustment,
      },
    }));
  }, [query.data]);

  const totals = query.data?.totals;
  /* `StockFlowBlock` bilan ayni sabab: qator O'ZI bo'sh emas (har kun
     uchun nuqta bor), shuning uchun JAMIGA qaraladi. */
  const empty =
    totals !== undefined && totals.receipt === 0 && totals.requisition === 0 && totals.adjustment === 0;

  return (
    <ReportCard
      icon={PackagePlus}
      tone="success"
      title={t('report.receiptTitle')}
      hint={t('report.receiptHint', { count: query.data?.eventCount ?? 0 })}
      pending={query.isPending}
      error={query.error ?? null}
      empty={empty}
      emptyText={t('report.receiptEmpty', { from: range.from, to: range.to })}
    >
      <div className="flex flex-col gap-4">
        <StackedBars points={points} series={series} caption={t('report.receiptTitle')} />

        {/*
          Bu qator IKKI vazifani bajaradi: grafikning izohi (rang → nom)
          va jamining o'zi. Grafik ostida alohida izoh ham chizilsa,
          ayni uchta yorliq ikki marta takrorlanardi — shuning uchun
          `StackedBars` da izoh umuman yo'q.
        */}
        <dl className="flex flex-wrap gap-x-6 gap-y-2 border-t border-border pt-3 text-sm">
          {series.map((item) => (
            <div key={item.key} className="flex items-baseline gap-2">
              <dt className="flex items-center gap-1.5 text-text-muted">
                <span
                  aria-hidden
                  className="size-2.5 rounded-full"
                  style={{ background: item.color }}
                />
                {item.label}
              </dt>
              <dd className="tabular font-medium text-text">
                {format(totals?.[item.key as keyof typeof totals] ?? 0)}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </ReportCard>
  );
};
